"use client";

import { useMemo, useState } from "react";
import { MapPin, Search } from "lucide-react";
import type { VitrineMapConfig } from "@/lib/vitrine/types";

// Démo "carte / annuaire" : liste filtrable des lieux cités par le brief,
// carte OpenStreetMap (iframe officielle, aucune dépendance) centrée sur le
// lieu sélectionné. Coordonnées géocodées à la génération.

function embedUrl(lat: number, lng: number): string {
  const d = 0.02;
  const bbox = [lng - d, lat - d, lng + d, lat + d].map((n) => n.toFixed(5)).join("%2C");
  return `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${lat.toFixed(5)}%2C${lng.toFixed(5)}`;
}

export function DemoMap({
  config,
  onInteract,
  onComplete,
}: {
  config: VitrineMapConfig;
  onInteract: () => void;
  onComplete: () => void;
}) {
  const points = useMemo(
    () => config.points.filter((p) => p.lat !== null && p.lng !== null),
    [config.points]
  );
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(0);
  const [clicks, setClicks] = useState(0);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return points
      .map((p, i) => ({ p, i }))
      .filter(({ p }) => !q || `${p.nom} ${p.adresse} ${p.description}`.toLowerCase().includes(q));
  }, [points, query]);

  const current = points[selected] ?? points[0];
  if (!current) return null;

  return (
    <div className="space-y-4">
      <div>
        <h3 className="font-display text-2xl text-ink">{config.titre}</h3>
        {config.intro && <p className="mt-1 text-sm text-ink/60">{config.intro}</p>}
      </div>
      <div className="grid gap-4 md:grid-cols-[2fr_3fr]">
        <div className="space-y-2">
          <label className="flex items-center gap-2 rounded-md border border-line bg-paper-pure px-3 py-2">
            <Search className="h-4 w-4 text-ink/40" />
            <input
              value={query}
              onChange={(e) => {
                onInteract();
                setQuery(e.target.value);
              }}
              placeholder="Rechercher une ville, un nom…"
              className="w-full bg-transparent text-sm text-ink outline-none placeholder:text-ink/40"
            />
          </label>
          <ul className="max-h-72 space-y-1 overflow-y-auto">
            {filtered.map(({ p, i }) => (
              <li key={i}>
                <button
                  type="button"
                  onClick={() => {
                    onInteract();
                    setSelected(i);
                    const n = clicks + 1;
                    setClicks(n);
                    if (n >= 2) onComplete();
                  }}
                  className={`flex w-full items-start gap-2 rounded-md p-2 text-left text-sm ${
                    i === selected ? "bg-gold/10" : "hover:bg-paper-alt"
                  }`}
                >
                  <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-gold" strokeWidth={1.8} />
                  <span>
                    <span className="block font-medium text-ink">{p.nom}</span>
                    <span className="block text-ink/60">{p.adresse}</span>
                    {p.description && <span className="block text-xs text-ink/50">{p.description}</span>}
                  </span>
                </button>
              </li>
            ))}
            {filtered.length === 0 && <li className="p-2 text-sm text-ink/50">Aucun résultat</li>}
          </ul>
        </div>
        <iframe
          key={selected}
          title={`Carte : ${current.nom}`}
          src={embedUrl(current.lat!, current.lng!)}
          className="h-72 w-full rounded-lg border border-line md:h-full md:min-h-72"
          loading="lazy"
        />
      </div>
    </div>
  );
}
